import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useAuth} from '../hooks/useAuth';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faList,
    faBolt,
    faCalculator,
    faMobile,
    faUsers,
    faBalanceScale,
    faStar,
    faMoneyBillWave,
    faArrowRight,
    faCheckCircle,
    faShield,
} from '@fortawesome/free-solid-svg-icons';
import {faGoogle} from '@fortawesome/free-brands-svg-icons';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import { useTranslation } from 'react-i18next';
import UserDropdown from "@/components/common/UserDropdown.tsx";
import Footer from "@/components/Layout/Footer.tsx";
import LoadingSpinner from "@/components/common/LoadingSpinner.tsx";
import TipsCard from "@/components/home/TipsCard.tsx";
import InstallAppButton from "@/components/home/InstallAppButton.tsx";
import {useDarkMode} from "@/hooks/useDarkMode.ts";
import {hasLocalHistory} from "@/hooks/useEventHistory.ts";
import CompactModeToggle from "@/components/common/CompactModeToggle.tsx";

const FEATURES: { key: string; icon: IconDefinition; iconClass: string }[] = [
    { key: 'realTimeSync', icon: faBolt, iconClass: 'text-yellow-600 dark:text-yellow-400' },
    { key: 'smartCalculations', icon: faCalculator, iconClass: 'text-purple-600 dark:text-purple-400' },
    { key: 'mobileOptimized', icon: faMobile, iconClass: 'text-green-600 dark:text-green-400' },
];

const HOW_IT_WORKS_STEPS: { key: string; icon: IconDefinition }[] = [
    { key: 'step1', icon: faUsers },
    { key: 'step2', icon: faMoneyBillWave },
    { key: 'step3', icon: faBalanceScale },
];

const HomePage: React.FC = () => {
    const {user, loading, signInWithGoogle} = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { darkMode, toggleDarkMode } = useDarkMode();
    const [joinInput, setJoinInput] = useState('');
    const [joinError, setJoinError] = useState(false);
    const [hasLocalEvents] = useState(hasLocalHistory);

    const handleJoinEvent = (e: React.FormEvent) => {
        e.preventDefault();
        const input = joinInput.trim();
        // Accept a full event URL or a bare event id
        const match = input.match(/\/event\/([^/?#\s]+)/);
        const eventId = match ? match[1] : input;
        if (!eventId || /[\s/]/.test(eventId)) {
            setJoinError(true);
            return;
        }
        navigate(`/event/${eventId}`);
    };

    if (loading) {
        return <LoadingSpinner/>;
    }

    // Join event by link or code
    const joinForm = (
        <form onSubmit={handleJoinEvent} className="max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
                <input
                    type="text"
                    value={joinInput}
                    onChange={(e) => {
                        setJoinInput(e.target.value);
                        setJoinError(false);
                    }}
                    placeholder={t('home.hero.joinPlaceholder')}
                    className="glass-input flex-1 px-5 py-4"
                />
                {/* Signed in this is the second way in, so it steps down to
                    secondary rather than sit as a twin of Create New Event */}
                <button
                    type="submit"
                    className={`${user ? 'glass-btn-secondary' : 'glass-btn-primary'} flex items-center justify-center px-8 py-4 text-lg`}
                >
                    {t('home.hero.joinButton')}
                    <FontAwesomeIcon icon={faArrowRight} className="ml-3"/>
                </button>
            </div>
            {joinError && (
                <p className="mt-2 text-sm text-red-500 dark:text-red-400 text-left">
                    {t('home.hero.joinError')}
                </p>
            )}
            {/* About joining, so it sits with the join form — under the Google
                button it seemed to say that signing up needs no sign-up */}
            {!user && (
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center">
                    <FontAwesomeIcon icon={faShield} className="mr-2"/>
                    {t('home.hero.noSignup')}
                </p>
            )}
        </form>
    );

    // On a phone the buttons take the join form's full width too, instead of
    // a column of three widths
    const accountActions = user ? (
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center max-w-xl mx-auto">
            <Link
                to="/create-event"
                className="glass-btn-primary flex items-center justify-center px-8 py-4 text-lg"
            >
                <FontAwesomeIcon icon={faPlus} className="mr-3"/>
                {t('home.actions.createNewEvent')}
            </Link>
            <Link
                to="/my-events"
                className="glass-btn-secondary flex items-center justify-center px-6 py-3"
            >
                <FontAwesomeIcon icon={faList} className="mr-3"/>
                {t('home.actions.viewMyEvents')}
            </Link>
        </div>
    ) : (
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center max-w-xl mx-auto">
            <button
                onClick={signInWithGoogle}
                className="glass-btn-primary flex items-center justify-center px-8 py-4 text-lg"
            >
                <FontAwesomeIcon icon={faGoogle} className="mr-3"/>
                {t('auth.getStartedFree')}
                <FontAwesomeIcon icon={faArrowRight} className="ml-3"/>
            </button>
            {/* Events opened here before signing in, or before signing out,
                are kept on the device */}
            {hasLocalEvents && (
                <Link
                    to="/my-events"
                    className="glass-btn-secondary flex items-center justify-center px-6 py-3"
                >
                    <FontAwesomeIcon icon={faList} className="mr-3"/>
                    {t('home.actions.viewMyEvents')}
                </Link>
            )}
        </div>
    );

    const orDivider = (
        <div className="flex items-center gap-4 max-w-xl mx-auto my-6 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex-1 h-px bg-gray-300/70 dark:bg-gray-700"/>
            {t('home.hero.or')}
            <span className="flex-1 h-px bg-gray-300/70 dark:bg-gray-700"/>
        </div>
    );

    return (
        <div className="glass-page-bg flex flex-col">
            {/* Header */}
            <header className="glass-header">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo */}
                        <Link to="/" className="flex items-center space-x-2.5 group min-w-0">
                            <div className="glass-header-icon w-10 h-10 flex-shrink-0 ring-2 ring-white/30 dark:ring-white/10">
                                <img src="/images/logo192.png" alt={t('app.name')} width={24} height={24} className="w-6 h-6 relative z-10"/>
                            </div>
                            <div className="leading-none min-w-0">
                                <h1 className="text-base sm:text-lg font-extrabold bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 bg-clip-text text-transparent truncate">
                                    {t('app.name')}
                                </h1>
                                <p className="hidden sm:block text-[10px] font-medium text-gray-400 dark:text-gray-500 tracking-wider uppercase mt-0.5 truncate">{t('app.tagline')}</p>
                            </div>
                        </Link>

                        {/* Right side actions — the same two on every screen size.
                            Theme, language and, for a guest, sign-in are all in
                            the user menu; the hero has its own sign-in button. */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Compact Mode Toggle */}
                            <CompactModeToggle />

                            {/* User menu */}
                            <UserDropdown darkMode={darkMode} onToggleDarkMode={toggleDarkMode}/>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1">
                <div className="relative">
                    {/* Hero Section */}
                    <section
                        className="relative py-12 md:py-20">
                        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="text-center">
                                {/* On a phone the header's logo is right above it */}
                                <div className="hidden sm:inline-flex items-center justify-center">
                                    <img src="/images/logo512.png" alt={t('app.name')} width={80} height={80} className="w-20 h-20"/>
                                </div>

                                <h1 className={`text-4xl md:text-6xl font-bold text-gray-900 dark:text-white ${user ? 'mb-8' : 'mb-6'}`}>
                                    {t('home.hero.title')}
                                    <span
                                        className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                        {t('home.hero.subtitle')}
                                    </span>
                                </h1>

                                {/* The pitch is for newcomers — someone signed in
                                    came back for their events */}
                                {!user && (
                                    <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
                                        {t('home.hero.description')}
                                    </p>
                                )}

                                {/* Signed out, joining someone's event comes first:
                                    it needs no account. Signed in, their own events
                                    do. "or" between the two says they are two ways
                                    in, not two steps. */}
                                {user ? (
                                    <>
                                        {accountActions}
                                        {orDivider}
                                        {joinForm}
                                    </>
                                ) : (
                                    <>
                                        {joinForm}
                                        {orDivider}
                                        {accountActions}
                                    </>
                                )}

                                {/* Only shows where the app can actually be installed */}
                                <InstallAppButton className="mt-6"/>
                            </div>
                        </div>
                    </section>

                    {/* Tip ngẫu nhiên - giúp người dùng biết app còn làm được gì */}
                    {user && <TipsCard/>}

                    {/* Marketing sections - chỉ hiển thị cho khách chưa đăng nhập */}
                    {!user && (
                        <>
                            {/* Features Section — below lg each card lies on its
                                side, icon beside the text: the three then fit on about
                                one phone screen instead of one and a half, and a tablet
                                is spared three columns too narrow for their titles */}
                            <section className="py-12 md:py-20">
                                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                                    <div className="text-center mb-10 md:mb-16">
                                        {/* The star flows with the text rather than sitting
                                            beside it in a flex row, where a title wrapped
                                            onto two lines left it floating off to the side */}
                                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 text-balance">
                                            <FontAwesomeIcon icon={faStar} className="mr-3 text-yellow-500"/>
                                            {t('home.features.title')}
                                        </h2>
                                        <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-balance">
                                            {t('home.features.subtitle')}
                                        </p>
                                    </div>

                                    <div className="grid lg:grid-cols-3 gap-4 lg:gap-8 max-w-2xl mx-auto lg:max-w-none">
                                        {FEATURES.map((feature) => (
                                            <div
                                                key={feature.key}
                                                className="glass-card glass-shimmer-hover flex items-start gap-4 p-5 lg:block lg:p-8 lg:text-center"
                                            >
                                                <div className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 lg:w-16 lg:h-16 glass-sub-panel rounded-xl lg:mb-6">
                                                    <FontAwesomeIcon icon={feature.icon}
                                                                     className={`text-xl lg:text-2xl ${feature.iconClass}`}/>
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-1 lg:mb-3">
                                                        {t(`home.features.${feature.key}.title`)}
                                                    </h3>
                                                    <p className="text-gray-600 dark:text-gray-300">
                                                        {t(`home.features.${feature.key}.description`)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </section>

                            {/* How it Works — numbered stops joined by a line: down
                                the side on a phone, across on wider screens. Stacked
                                and centred, each step wearing both a number and a large
                                icon, the three read as a list of repeated blocks. */}
                            <section className="py-12 md:py-20">
                                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                                    <div className="text-center mb-10 md:mb-16">
                                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                                            {t('home.howItWorks.title')}
                                        </h2>
                                        <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300">
                                            {t('home.howItWorks.subtitle')}
                                        </p>
                                    </div>

                                    <ol className="max-w-md mx-auto md:max-w-none md:grid md:grid-cols-3 md:gap-8">
                                        {HOW_IT_WORKS_STEPS.map((step, i) => (
                                            <li
                                                key={step.key}
                                                className="relative flex gap-4 pb-8 last:pb-0 md:flex-col md:items-center md:gap-0 md:pb-0 md:text-center"
                                            >
                                                {/* The line on to the next step, stopping short
                                                    of both numbers. Across, it runs from this
                                                    column's centre to the next one's: 2rem is the
                                                    grid gap, 2.25rem the number's radius plus the
                                                    space left around it. */}
                                                {i < HOW_IT_WORKS_STEPS.length - 1 && (
                                                    <span
                                                        aria-hidden="true"
                                                        className="absolute left-5 top-12 bottom-2 w-0.5 -translate-x-1/2 rounded-full bg-gradient-to-b from-indigo-400/60 to-purple-400/20 md:left-[calc(50%+2.25rem)] md:right-[calc(-50%-2rem+2.25rem)] md:top-6 md:bottom-auto md:w-auto md:h-0.5 md:translate-x-0 md:-translate-y-1/2 md:bg-gradient-to-r"
                                                    />
                                                )}
                                                <span
                                                    className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full text-white font-bold md:text-lg"
                                                    style={{background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', boxShadow: '0 4px 16px var(--accent-glow)'}}
                                                >
                                                    {i + 1}
                                                </span>
                                                <div className="min-w-0 pt-1.5 md:pt-0 md:mt-5">
                                                    <h3 className="flex items-center md:justify-center gap-2 text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                                                        <FontAwesomeIcon icon={step.icon} className="text-base text-indigo-500 dark:text-indigo-400" aria-hidden="true"/>
                                                        {t(`home.howItWorks.${step.key}.title`)}
                                                    </h3>
                                                    <p className="mt-1 md:mt-2 text-gray-600 dark:text-gray-300">
                                                        {t(`home.howItWorks.${step.key}.description`)}
                                                    </p>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            </section>

                            {/* The tips are about the inside of an event: worth
                                more once the steps above have said what one is */}
                            <TipsCard/>

                            {/* CTA Section */}
                            <section className="py-12 md:py-20 glass-cta rounded-3xl mx-4 mb-8">
                                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-balance">
                                        {t('home.cta.title')}
                                    </h2>
                                    <p className="text-lg md:text-xl text-blue-100 mb-8 text-balance">
                                        {t('home.cta.subtitle')}
                                    </p>
                                    <button
                                        onClick={signInWithGoogle}
                                        className="glass-btn-secondary inline-flex items-center px-8 py-4 text-lg !bg-white/90 !text-indigo-600 hover:!bg-white"
                                    >
                                        <FontAwesomeIcon icon={faGoogle} className="mr-3"/>
                                        {t('home.cta.button')}
                                        <FontAwesomeIcon icon={faArrowRight} className="ml-3"/>
                                    </button>

                                    {/* Hidden on a phone, where the three stack into a
                                        column of lines saying again what the feature cards
                                        and the line above already said */}
                                    <div className="mt-8 hidden sm:flex flex-wrap justify-center items-center gap-6 text-blue-100">
                                        <div className="flex items-center">
                                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2"/>
                                            <span>{t('home.cta.benefits.free')}</span>
                                        </div>
                                        <div className="flex items-center">
                                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2"/>
                                            <span>{t('home.cta.benefits.realTime')}</span>
                                        </div>
                                        <div className="flex items-center">
                                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2"/>
                                            <span>{t('home.cta.benefits.mobile')}</span>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </main>

            {/* Footer */}
            <Footer/>
        </div>
    );
};

export default HomePage;
